/**
 * `CloudflareInstallPanel` — body of the `cloudflare` install-method
 * tab inside `InstallMethodTabs` (ADR-0189).
 *
 * Merchants whose site is proxied through Cloudflare can deploy a
 * managed Sill Worker directly from onboarding via a one-time API
 * token paste. The Worker serves `/.well-known/agent-card.json` and
 * logs AI-crawler hits at the network layer — no JavaScript embed
 * required.
 *
 * After the Worker deploys, the merchant clicks the "Verify" control in
 * `ProofStatusPanel` (above the tabs) to confirm domain ownership.
 * Because the CF Worker passes all non-special requests to origin
 * unchanged (`return fetch(request)`), the proof check can find the
 * Sill snippet on the origin HTML if they've also added the script tag
 * — or they can use the `.well-known/sill-proof.txt` fallback.
 *
 * States:
 *   - idle    — token form + instructions
 *   - loading — deploy in progress
 *   - done    — Worker live; calls onInstalled? + shows success panel
 *
 * Error types surfaced inline:
 *   - cloudflare_auth_failed → "Token invalid — check permissions"
 *   - no_zone_for_domain    → domain not found in that CF account
 *   - Other → coral toast (same as Settings → Integrations)
 */

import { ArrowRight, Check, Cloud, ExternalLink, Loader2, RefreshCw, TriangleAlert } from 'lucide-react';
import { useCallback, useState } from 'react';

import type { Site } from '@sill/types';

import { ApiError } from '../../api/http-client.js';
import { useApi } from '../../api/api-context.js';
import { useToast } from '../../app/providers.js';
import { workerDeployedBody } from './install-copy.js';
import { Collapsible, StepHeading } from '../../components/atoms/index.js';
import {
  CLOUDFLARE_ROUTE_IN_USE_REASON,
  cloudflareRouteInUseMessage,
} from '../../lib/cloudflare-route-in-use.js';
import { useC } from '../../lib/theme.js';
import type { UseProofCheckState } from '../../hooks/useProofCheck.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface CloudflareInstallPanelProps {
  readonly site: Site;
  /**
   * ADR-0212 slice C — rendered inside the WordPress / WooCommerce tab's
   * Step 1, which supplies its own kicker and sentence; the panel then
   * starts at the token box.
   */
  readonly nested?: boolean;
  /** Shared proof-check state — used to render the Verify button inline
   *  in the deployed state (so the merchant doesn't need to find a
   *  separate Verify control elsewhere). */
  readonly proofCheck?: UseProofCheckState;
  /** Seed the deployed state from parent (survives tab-switch remounts). */
  readonly initialDeployed?: boolean;
  readonly initialZoneName?: string | null;
  /** Called once when the Worker first deploys so the parent can persist
   *  the deployed state across tab switches. */
  readonly onDeployed?: (zoneName: string | null) => void;
  /** Called when the deploy response returns `domain_verified: true`
   *  (server recorded proof automatically — no separate Verify click
   *  needed). Caller uses this to advance the onboarding flow. */
  readonly onDomainVerified?: () => void;
}

export function CloudflareInstallPanel({
  site,
  nested = false,
  proofCheck,
  initialDeployed = false,
  initialZoneName = null,
  onDeployed,
  onDomainVerified,
}: CloudflareInstallPanelProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();

  const [apiToken, setApiToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [deployed, setDeployed] = useState(initialDeployed);
  const [zoneName, setZoneName] = useState<string | null>(initialZoneName);
  const [connectError, setConnectError] = useState<string | null>(null);
  // Set to true when the deploy response includes `domain_verified: true`
  // (server recorded proof automatically on successful Worker deploy).
  const [domainVerified, setDomainVerified] = useState(false);

  const handleDeploy = useCallback(async (): Promise<void> => {
    if (connecting || deployed) return;
    const token = apiToken.trim();
    if (token.length === 0) {
      setConnectError('Paste your Cloudflare API token to continue.');
      return;
    }
    setConnectError(null);
    setConnecting(true);
    try {
      const result = await api.sites.connectCloudflare(site.site_id, token);
      const zn = result.cf_zone_name ?? null;
      setZoneName(zn);
      setDeployed(true);
      onDeployed?.(zn);
      // No `onInstalled` here (ADR-0212): that callback means "the first
      // beacon arrived" and triggers an immediate auto-verify, which a
      // fresh Worker cannot pass until CF's edge propagates. Proof from
      // a deploy flows through `onDomainVerified` below.
      if (result.domain_verified === true) {
        setDomainVerified(true);
        onDomainVerified?.();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.server_reason === 'cloudflare_auth_failed') {
          setConnectError('Token invalid — check it has the required permissions.');
          return;
        }
        // Install 1.20 — the domain's {domain}/* route runs the merchant's
        // own Worker; Sill leaves it alone rather than switching it off.
        if (err.server_reason === CLOUDFLARE_ROUTE_IN_USE_REASON) {
          setConnectError(cloudflareRouteInUseMessage(err.server_body));
          return;
        }
        if (err.server_reason === 'no_zone_for_domain') {
          setConnectError(
            "This token is valid, but this domain isn't in your Cloudflare account. Make sure the domain is added to Cloudflare and try again.",
          );
          return;
        }
        toast({
          title: 'Could not deploy Cloudflare Worker',
          body: err.message.length > 0 ? err.message : 'Unknown error.',
          tone: 'coral',
        });
        return;
      }
      toast({
        title: 'Could not deploy Cloudflare Worker',
        body: 'Unknown error',
        tone: 'coral',
      });
    } finally {
      setConnecting(false);
    }
  }, [api, apiToken, connecting, deployed, onDomainVerified, site.site_id, toast]);

  if (deployed) {
    return (
      <div>
        <div
          className="flex items-center gap-2 mb-3"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            color: C.limeFg,
          }}
        >
          <Cloud size={12} />
          WORKER ACTIVE
        </div>
        <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 16 }}>
          {workerDeployedBody(zoneName)}
        </p>

        {domainVerified || proofCheck?.verified ? (
          <div
            className="flex items-center gap-2"
            style={{ fontFamily: monoFont, fontSize: 11, letterSpacing: 1.5, color: C.limeFg }}
          >
            <Check size={12} /> DOMAIN VERIFIED
          </div>
        ) : proofCheck !== undefined ? (
          <div>
            <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginBottom: 10 }}>
              Verify to finish.
            </p>
            <button
              type="button"
              onClick={() => void proofCheck.verify()}
              disabled={proofCheck.checking}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 14,
                color: proofCheck.checking ? C.muted : C.ink,
                background: proofCheck.checking ? C.surface2 : C.lime,
                border: `1px solid ${proofCheck.checking ? C.border : C.lime}`,
                borderRadius: 2,
                padding: '10px 20px',
                cursor: proofCheck.checking ? 'default' : 'pointer',
              }}
            >
              {proofCheck.checking ? (
                <>
                  <RefreshCw size={15} style={{ animation: 'spin 1.4s linear infinite' }} />
                  Checking…
                </>
              ) : (
                <>
                  Verify <ArrowRight size={15} />
                </>
              )}
            </button>
          </div>
        ) : (
          <div
            className="flex items-start gap-2 p-3"
            style={{ border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface2 }}
          >
            <Check size={14} color={C.limeFg} style={{ marginTop: 2, flexShrink: 0 }} />
            <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, margin: 0 }}>
              Verify above to finish.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* ADR-0212 — above the fold: one sentence, the token box, Deploy.
          The permissions table (the panel's biggest block) and the
          what-it-does essay sit behind two inline disclosures.
          Founder 2026-10-20: the "STEP 1 · PASTE A CLOUDFLARE API TOKEN"
          kicker was removed from the standalone (non-nested) Cloudflare
          tab — the one-click deploy IS the whole job, so a "Step 1" with
          nothing after reads as a missing Step 2. Numbered steps stay
          where there really are several (manual script path, Woo).
          Founder 2026-10-21: the standalone tab adds a plain heading
          (no badge) so the hierarchy matches the other single-step tabs
          (WordPress plugin). */}
      {nested ? null : (
        <>
          <StepHeading>Deploy the Cloudflare Worker</StepHeading>
          <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.55, margin: '0 0 14px', maxWidth: 560 }}>
            The Worker runs in front of your site, so it sees every request.
          </p>
        </>
      )}
      {/* One disclosure per step (ADR-0212): the chip strip above the
          panel already says what the Worker covers, so only the token
          how-to hides here. */}
      <div style={{ marginBottom: 14 }}>
        <Collapsible variant="inline" label="How to create the token">
          <div style={{ marginTop: 2 }}>
      <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
        In your{' '}
        <a
          href="https://dash.cloudflare.com/profile/api-tokens/create"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1"
          style={{ color: C.text, textDecoration: 'underline' }}
        >
          Cloudflare API Tokens dashboard
          <ExternalLink size={11} />
        </a>
        , create a Custom token with these three permissions:
      </p>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          lineHeight: 1.7,
          color: C.text,
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          padding: '8px 12px',
          marginBottom: 20,
        }}
      >
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ color: C.muted }}>
              <th style={{ textAlign: 'left', fontWeight: 400, paddingRight: 24, paddingBottom: 4 }}>Category</th>
              <th style={{ textAlign: 'left', fontWeight: 400, paddingRight: 24, paddingBottom: 4 }}>Resource</th>
              <th style={{ textAlign: 'left', fontWeight: 400, paddingBottom: 4 }}>Level</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ paddingRight: 24 }}>Account</td>
              <td style={{ paddingRight: 24 }}>Workers Scripts</td>
              <td>Edit</td>
            </tr>
            <tr>
              <td style={{ paddingRight: 24 }}>Zone</td>
              <td style={{ paddingRight: 24 }}>Workers Routes</td>
              <td>Edit</td>
            </tr>
            <tr>
              <td style={{ paddingRight: 24 }}>Zone</td>
              <td style={{ paddingRight: 24 }}>Zone</td>
              <td>Read</td>
            </tr>
          </tbody>
        </table>
      </div>

          </div>
        </Collapsible>
      </div>

      <label
        htmlFor="onboarding-cf-token"
        style={{
          display: 'block',
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 500,
          color: C.text,
          marginBottom: 6,
        }}
      >
        API token
      </label>
      <input
        id="onboarding-cf-token"
        type="password"
        value={apiToken}
        onChange={(e) => {
          setApiToken(e.target.value);
          if (connectError !== null) setConnectError(null);
        }}
        placeholder="Paste your Cloudflare API token"
        autoComplete="off"
        style={{
          display: 'block',
          width: '100%',
          fontFamily: monoFont,
          fontSize: 12,
          color: C.text,
          background: C.surface2,
          border: `1px solid ${connectError !== null ? C.coralFg : C.border}`,
          borderRadius: 2,
          padding: '8px 10px',
          marginBottom: connectError !== null ? 6 : 12,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      {connectError !== null ? (
        <div
          className="flex items-start gap-2 mb-3"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, lineHeight: 1.5 }}
        >
          <TriangleAlert size={13} style={{ marginTop: 2, flexShrink: 0 }} />
          {connectError}
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => void handleDeploy()}
        disabled={connecting}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: sansFont,
          fontWeight: 600,
          fontSize: 14,
          // `C.ink` (not `C.bg`) — bg is cream in light mode, which put
          // white text on the lime button (founder 2026-08-04 screenshot).
          color: connecting ? C.muted : C.ink,
          background: connecting ? C.surface2 : C.lime,
          border: `1px solid ${connecting ? C.border : C.lime}`,
          borderRadius: 2,
          padding: '10px 20px',
          cursor: connecting ? 'default' : 'pointer',
        }}
      >
        {connecting ? (
          <>
            <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
            Deploying Worker…
          </>
        ) : (
          <>
            <Cloud size={15} />
            Deploy Cloudflare Worker
          </>
        )}
      </button>
    </div>
  );
}
